import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n, SUPPORTED_LANGS } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { LogOut, Menu, X } from "lucide-react";
import { trackMarketing } from "@/lib/marketing";
import ThemeSwitcher from "@/components/ThemeSwitcher";

const NAV_ITEMS = [
  { href: "#product", key: "nav_product" },
  { href: "#documents", key: "nav_documents" },
  { href: "#how", key: "nav_how" },
  { href: "#use-cases", key: "nav_use_cases" },
  { href: "/pricing", key: "nav_pricing", route: true },
  { href: "/demo", key: "nav_demo", route: true },
  { href: "#about", key: "nav_about" },
  { href: "#contact", key: "nav_contact" },
];

export default function Navbar() {
  const { lang, setLang, t } = useI18n();
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === "/";
  const destination = (item) => item.route ? item.href : `${isHome ? "" : "/"}${item.href}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "bg-[var(--td-bg)]/95 backdrop-blur-xl hairline-bottom" : "bg-transparent"
      }`}
      data-testid="landing-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 h-[64px] md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group shrink-0 mr-3">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[var(--td-gold)] rotate-45 group-hover:rotate-[135deg] transition-transform duration-500 shrink-0" />
          <span className="font-serif text-lg min-[390px]:text-xl md:text-2xl tracking-tight whitespace-nowrap">
            TradeDocs<span className="text-[var(--td-gold)]"> · </span>Desk
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <ThemeSwitcher publicSite />
          <label className="relative border border-[var(--td-border)]" data-testid="lang-toggle">
            <span className="sr-only">Language</span>
            <select
              value={lang}
              onChange={(event) => setLang(event.target.value)}
              className="h-8 bg-[var(--td-bg)] text-[var(--td-gold)] font-mono text-[10px] uppercase tracking-widest pl-2.5 pr-7 outline-none cursor-pointer"
            >
              {SUPPORTED_LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </label>
          {!loading && (user ? <>
            <button
              type="button"
              onClick={logout}
              data-testid="nav-signout"
              className="text-sm text-[var(--td-text-2)] hover:text-red-400 transition-colors px-2 inline-flex items-center gap-1.5"
            >
              <LogOut size={14} /> {t("nav_logout")}
            </button>
            <Link
              to="/app/dashboard"
              data-testid="nav-workspace"
              className="btn-gold text-sm !py-2 !px-4"
            >
              {t("nav_workspace")}
            </Link>
          </> : <>
            <Link
              to="/signin"
              data-testid="nav-signin"
              className="text-sm text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors px-2"
            >
              {t("nav_login")}
            </Link>
            <Link
              to="/signup"
              onClick={() => trackMarketing("signup_cta", { page: isHome ? "home" : "other", language: lang })}
              data-testid="nav-start"
              className="btn-gold text-sm !py-2 !px-4"
            >
              {t("nav_join")}
            </Link>
          </>)}
        </div>

        <button
          className="xl:hidden w-11 h-11 shrink-0 inline-flex items-center justify-center text-[var(--td-text)] border border-[var(--td-border)]"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div className="hidden xl:flex flex-wrap items-center justify-center gap-4 px-4 pb-3 text-xs text-[var(--td-text-2)]">
        {NAV_ITEMS.map((it) => <a key={it.href} href={destination(it)}
          onClick={() => it.href === "/demo" && trackMarketing("demo_cta", { page: isHome ? "home" : "other", language: lang })}
          className="hover:text-[var(--td-gold)] transition-colors">{t(it.key)}</a>)}
      </div>

      {mobileOpen && (
        <div className="xl:hidden border-t border-[var(--td-border)] bg-[var(--td-bg)] max-h-[calc(100dvh-72px)] overflow-y-auto">
          <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
            <ThemeSwitcher publicSite />
            {NAV_ITEMS.map((it) => (
              <a
                key={it.href}
                href={destination(it)}
                onClick={() => { setMobileOpen(false); if (it.href === "/demo") trackMarketing("demo_cta", { page: isHome ? "home" : "other", language: lang }); }}
                className="text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors"
              >
                {t(it.key)}
              </a>
            ))}
            <div className="flex flex-wrap items-center gap-1 font-mono text-[10px] uppercase tracking-widest border border-[var(--td-border)] p-0.5 self-start">
              {SUPPORTED_LANGS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-2 py-1 transition-colors ${
                    lang === l ? "bg-[var(--td-gold)] text-[var(--td-bg)]" : "text-[var(--td-text-2)]"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {!loading && (user ? <>
              <Link to="/app/dashboard" onClick={() => setMobileOpen(false)} className="btn-gold text-sm text-center !py-3 !px-4">
                {t("nav_workspace")}
              </Link>
              <button type="button" onClick={async () => { await logout(); setMobileOpen(false); }} className="text-left text-[var(--td-text-2)] hover:text-red-400 inline-flex items-center gap-2">
                <LogOut size={15} /> {t("nav_logout")}
              </button>
            </> : <>
              <Link to="/signin" className="text-[var(--td-text-2)] hover:text-[var(--td-gold)]">
                {t("nav_login")}
              </Link>
              <Link to="/signup" onClick={() => { setMobileOpen(false); trackMarketing("signup_cta", { page: isHome ? "home" : "other", language: lang }); }} className="btn-gold text-sm text-center !py-2 !px-4">
                {t("nav_join")}
              </Link>
            </>)}
          </div>
        </div>
      )}
    </nav>
  );
}
